import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { pageApi, historyApi } from '../services/api';
import { useBlocks } from '../hooks/useBlocks';
import BlockComponent from './BlockComponent';
import { BLOCK_TYPES } from '../utils/blockTypes';
import { FaEdit, FaArrowLeft, FaHistory, FaUndo, FaHome } from 'react-icons/fa';
import '../styles/NotionPageView.css';

const NotionPageView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const historyId = searchParams.get('history');

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [coverImage, setCoverImage] = useState('');
  const [pageIcon, setPageIcon] = useState('');
  const [title, setTitle] = useState('');
  const [viewingHistory, setViewingHistory] = useState(false);
  const [historySnapshot, setHistorySnapshot] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const isAuthenticated = localStorage.getItem('token') && localStorage.getItem('user');

  const {
    blocks,
    loading: blocksLoading,
    error: blocksError,
    pageId,
    loadBlocks
  } = useBlocks(slug, historyId);

  useEffect(() => {
    const loadPage = async () => {
      try {
        setLoading(true);

        if (historyId) {
          setViewingHistory(true);
          await loadHistoricalSnapshot(historyId);
        } else {
          setViewingHistory(false);
          const result = await pageApi.getBySlug(slug, historyId);

          if (!result || !result.data) {
            throw new Error('Page not found');
          }

          setPage(result.data);
          setTitle(result.data?.page?.title || 'Untitled');
          setCoverImage(result.data?.page?.cover_image || '');
          setPageIcon(result.data?.page?.icon || '');
        }
      } catch (error) {
        console.error('Failed to load page:', error);
        alert(`Failed to load page: ${error.message}`);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadPage();
    }
  }, [slug, navigate, historyId]);

  const loadHistoricalSnapshot = async (snapshotId) => {
    try {
      const response = await historyApi.getHistoryById(snapshotId);
      const historyData = response.data.data;

      if (!historyData) {
        throw new Error('Snapshot data not found');
      }

      setHistorySnapshot(historyData);

      const pageResult = await pageApi.getBySlug(slug);
      if (pageResult && pageResult.data) {
        setPage(pageResult.data);
        setTitle(pageResult.data?.page?.title || 'Untitled');
        setCoverImage(pageResult.data?.page?.cover_image || '');
        setPageIcon(pageResult.data?.page?.icon || '');
      }
    } catch (error) {
      console.error('Failed to load snapshot:', error);
      alert('Failed to load historical snapshot');
      navigate(`/page/${slug}`);
    }
  };

  const handleRestore = async () => {
    if (!historyId) return;

    if (window.confirm('Restore to this version? This will overwrite the current page.')) {
      try {
        setRestoring(true);
        await historyApi.restoreSnapshot(historyId);
        alert('Page restored successfully!');
        navigate(`/page/${slug}`);
      } catch (error) {
        console.error('Failed to restore:', error);
        alert('Failed to restore page. Please try again.');
      } finally {
        setRestoring(false);
      }
    }
  };

  const getDisplayBlocks = () => {
    if (viewingHistory && historySnapshot?.snapshot_data?.blocks) {
      return historySnapshot.snapshot_data.blocks;
    }
    return blocks || [];
  };

  const getListNumberForBlock = (blockIndex, listGroup) => {
    const displayBlocks = getDisplayBlocks();
    if (!displayBlocks || blockIndex < 0 || blockIndex >= displayBlocks.length) return 1;

    let listNumber = 1;
    let currentIndex = blockIndex - 1;

    if (listGroup) {
      while (currentIndex >= 0) {
        const prevBlock = displayBlocks[currentIndex];
        if (prevBlock?.type === BLOCK_TYPES.NUMBERED_LIST &&
          prevBlock.properties?.listGroup === listGroup) {
          listNumber++;
          currentIndex--;
        } else {
          break;
        }
      }
    } else {
      while (currentIndex >= 0) {
        if (displayBlocks[currentIndex]?.type === BLOCK_TYPES.NUMBERED_LIST) {
          listNumber++;
          currentIndex--;
        } else {
          break;
        }
      }
    }

    return listNumber;
  };

  if (loading || blocksLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading page...</p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="error-container">
        <h2>Page Not Found</h2>
        <p>Unable to load page with slug: {slug}</p>
        <button
          onClick={() => navigate(isAuthenticated ? '/' : '/login')}
          className="btn-primary"
        >
          {isAuthenticated ? 'Go Home' : 'Go to Login'}
        </button>
      </div>
    );
  }

  const displayBlocks = getDisplayBlocks();

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`page-view-container ${!isAuthenticated ? 'public-view' : ''}`}>
        {/* Header - Different for authenticated vs public */}
        {isAuthenticated && (
          <div className="page-view-header">
            <>
              <button onClick={() => navigate('/')} className="back-button">
                <FaArrowLeft /> Back to Home
              </button>

              <div className="view-actions">
                {viewingHistory && (
                  <>
                    <button
                      className="restore-history-btn"
                      onClick={handleRestore}
                      disabled={restoring}
                    >
                      <FaUndo /> {restoring ? 'Restoring...' : 'Restore this Version'}
                    </button>
                    <button
                      className="view-current-btn"
                      onClick={() => navigate(`/page/${slug}`)}
                    >
                      View Current Version
                    </button>
                  </>
                )}

                {!viewingHistory && (
                  <Link to={`/page/edit/${slug}`} className="edit-page-button">
                    <FaEdit /> Edit Page
                  </Link>
                )}
              </div>
            </>
          </div>
        )}

        {viewingHistory && historySnapshot && (
          <div className="history-banner">
            <div className="history-banner-content">
              <FaHistory />
              <div className="history-info">
                <strong>Viewing historical version</strong>
                <span>
                  Saved on {new Date(historySnapshot.created_at).toLocaleDateString()} at{' '}
                  {new Date(historySnapshot.created_at).toLocaleTimeString()}
                </span>
              </div>
            </div>
          </div>
        )}

        {coverImage && (
          <div className="page-cover-view">
            <img src={coverImage} alt="Page cover" />
          </div>
        )}

        <div className="page-content-view">
          <div className="page-title-section">
            {pageIcon && (
              <div className="page-icon-view">
                {pageIcon.startsWith('data:') || pageIcon.startsWith('http') || pageIcon.startsWith('blob:') ? (
                  <img src={pageIcon} alt="Page icon" />
                ) : (
                  <span className="page-icon-emoji">{pageIcon}</span>
                )}
              </div>
            )}

            <h1 className="page-title-view">{title}</h1>
            {viewingHistory && (
              <div className="history-note">
                <em>This is a historical snapshot and cannot be edited</em>
              </div>
            )}

          </div>

          <div className="blocks-view-container">
            {!displayBlocks || displayBlocks.length === 0 ? (
              <div className="empty-page-view">
                <div className="empty-icon">📄</div>
                <h3>This page is empty</h3>
                <p>No content has been added to this page yet.</p>
                {isAuthenticated && !viewingHistory && (
                  <Link to={`/page/edit/${slug}`} className="btn-primary">
                    <FaEdit /> Add Content
                  </Link>
                )}
              </div>
            ) : (
              displayBlocks.map((block, index) => (
                <BlockComponent
                  key={block.id || `block-${index}`}
                  block={block}
                  isSelected={false}
                  onUpdate={() => { }}
                  onDelete={() => { }}
                  onSelect={() => { }}
                  onAddBelow={() => { }}
                  onAddAbove={() => { }}
                  onMoveBlock={() => { }}
                  onDragStart={() => { }}
                  onDragEnd={() => { }}
                  onGetListNumber={() => getListNumberForBlock(index, block.properties?.listGroup)}
                  placeholder=""
                  isFirst={index === 0}
                  isLast={index === displayBlocks.length - 1}
                  index={index}
                  totalBlocks={displayBlocks.length}
                  dragOverIndex={null}
                  isDragging={false}
                  isReadOnly={true}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default NotionPageView;